import { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Warning, CircleNotch } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import LienConnexionForm from '../components/shared/LienConnexionForm';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// RP09 (D10 C) — lien d'accès direct reçu par e-mail : ouvre la session sans mot de
// passe puis affiche l'écran visé (destination enregistrée côté serveur).
export function destinationSure(dest) {
  if (typeof dest !== 'string' || !dest.startsWith('/') || dest.startsWith('//') || dest.includes('\\')) {
    return '/dashboard';
  }
  return dest;
}

export default function AccesDirect() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { checkAuth } = useAuth();
  const [erreur, setErreur] = useState('');
  const dejaTente = useRef(false);

  useEffect(() => {
    if (dejaTente.current) return; // le lien est à usage unique : un seul appel
    dejaTente.current = true;
    (async () => {
      try {
        const { data } = await axios.post(`${API_URL}/api/auth/acces`, { token }, { withCredentials: true });
        const dest = destinationSure(data?.destination);
        if (dest.startsWith('/terrain')) {
          window.location.replace(dest);
          return;
        }
        await checkAuth();
        navigate(dest, { replace: true });
      } catch (err) {
        // Lien déjà utilisé mais session encore ouverte sur cet appareil : on continue.
        try {
          await axios.get(`${API_URL}/api/auth/me`, { withCredentials: true });
          await checkAuth();
          navigate('/dashboard', { replace: true });
          return;
        } catch { /* pas de session */ }
        const detail = err?.response?.data?.detail;
        if (err?.response?.status === 429) setErreur('Trop de tentatives. Réessayez dans quelques minutes.');
        else if (detail && typeof detail === 'object' && detail.message) setErreur(detail.message);
        else setErreur("Ce lien n'est pas valide. Demandez un nouveau lien de connexion.");
      }
    })();
  }, [token, navigate, checkAuth]);

  if (!erreur) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="acces-direct-chargement">
        <div className="text-center">
          <CircleNotch size={32} className="animate-spin text-[#C75B39] mx-auto mb-3" />
          <p className="text-sm text-[#6B6B6B]">Connexion en cours…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8] p-4" data-testid="acces-direct-erreur">
      <div className="max-w-md w-full p-8 bg-white border border-slate-200">
        <div className="text-center">
          <Warning size={40} className="mx-auto text-[#8B3A3A] mb-3" weight="fill" />
          <h1 className="text-xl font-semibold mb-2 text-[#2C2C2C]">Lien de connexion</h1>
          <p className="text-sm text-[#6B6B6B] mb-6">{erreur}</p>
        </div>
        <LienConnexionForm origine="bureau" />
        <p className="text-center text-xs text-[#6B6B6B] mt-6">
          <Link to="/login" className="text-[#C75B39] hover:underline">Se connecter avec un mot de passe</Link>
        </p>
      </div>
    </div>
  );
}
